import { useCallback, useEffect, useState } from 'react'
import { Clock, Workflow } from 'lucide-react'
import type { PipelineStage } from './types'
import { fetchStages } from './useAdminApi'
import { AdminTable, Banner, SectionHeader, Td, Th } from './ui'
import { reportCard, reportCardPad } from '../reports/ui'

export function StagesSection() {
  const [stages, setStages] = useState<PipelineStage[]>([])
  const [topics, setTopics] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchStages()
      setStages(data.stages)
      setTopics(data.topics)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pipeline stages')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Pipeline stages"
        subtitle="Read-only view from db/flow/topology.yaml — the authored manifest of stream processing stages."
      />
      <div className={reportCard + ' ' + reportCardPad}>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Workflow size={16} className="text-slate-400" />
          <span>{stages.length} stages</span>
          <span className="text-slate-300">·</span>
          <span>{topics.length} topics</span>
          <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
            Read-only
          </span>
        </div>
      </div>
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Stage</Th>
              <Th>Engine</Th>
              <Th>Reads</Th>
              <Th>Writes</Th>
              <Th>Stores</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stages.map((s) => (
              <tr key={s.id} className="align-top hover:bg-slate-50/80">
                <Td>
                  <div className="font-medium text-slate-900">{s.label}</div>
                  <div className="font-mono text-xs text-slate-400">{s.id}</div>
                  {s.timer ? (
                    <span className="mt-1 inline-flex items-center gap-0.5 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                      <Clock size={10} /> timer
                    </span>
                  ) : null}
                  {s.note ? (
                    <p className="mt-1 max-w-xs text-xs text-slate-500">
                      {s.note.length > 120 ? `${s.note.slice(0, 120)}…` : s.note}
                    </p>
                  ) : null}
                </Td>
                <Td>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{s.impl ?? '—'}</span>
                  {s.shadow ? <div className="mt-1 font-mono text-[10px] text-slate-400">shadow: {s.shadow}</div> : null}
                </Td>
                <Td className="max-w-[160px] whitespace-normal">
                  <TopicList items={s.reads} />
                </Td>
                <Td className="max-w-[160px] whitespace-normal">
                  <TopicList items={s.writes} />
                </Td>
                <Td className="max-w-[140px] whitespace-normal">
                  <TopicList items={s.stores} muted />
                </Td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
      )}
    </div>
  )
}

function TopicList({ items, muted }: { items: string[]; muted?: boolean }) {
  if (!items.length) return <span className="text-slate-300">—</span>
  return (
    <ul className="space-y-0.5">
      {items.map((t) => (
        <li key={t} className={`font-mono text-[10px] leading-tight ${muted ? 'text-slate-400' : 'text-slate-600'}`}>
          {t}
        </li>
      ))}
    </ul>
  )
}
